import React, { useEffect } from 'react'
import { useStoreStore } from '../hooks/useStore';
import { useGetStore } from '../hooks/useGetStore';
import { useLocation } from '@tanstack/react-router'
import appRoutes from '@/common/configs/appRoutes';
import { useCart } from '@/components/cart/hooks/useCart';
import { useGetZMPSetting } from '@/common/hooks/useGetZMPSetting';
import { useStorefront } from '@/common/storefront/use-storefront'
import { createStorefrontPath } from '@/common/storefront/storefront.config'
import {
  persistStoreMenuContext,
  readStoreMenuContext,
} from '@/common/storefront/store-menu-context'
import { queryClient } from '@/common/query/query-client'

interface Props {
  children: React.ReactNode;
}

/**
 * AppStoreProvider:
 * - Lấy storeNo từ URL và fetch thông tin cửa hàng.
 * - Tự động refetch khi người dùng vào các route cụ thể.
 * - Cập nhật lại store vào global state nếu có dữ liệu mới.
 */
const AppStoreProvider: React.FC<Props> = ({ children }) => {
  const storefront = useStorefront()
  const pathsToRefetch = React.useMemo<string[]>(
    () => [
      createStorefrontPath('/', storefront),
      createStorefrontPath(
        appRoutes.paths.en.category.root as `/${string}`,
        storefront,
      ),
      createStorefrontPath('/checkout', storefront),
    ],
    [storefront],
  )
  const { data: settings } = useGetZMPSetting()
  const pathname = useLocation({ select: (location) => location.pathname })
  const searchStr = useLocation({ select: (location) => location.searchStr })
  const { store, table_no, setStore, setTableNo, resetStore } = useStoreStore()
  const { initCart, updateCore } = useCart()

  // const tlv = buildRawTableNoTLV({
  //   floor: '02',
  //   zone: 'B1',
  //   table: '007',
  //   version: 'v1.0',
  // });

  // console.log('AppStoreProvider: tlv', tlv)

  const searchParams = React.useMemo(
    () => new URLSearchParams(searchStr),
    [searchStr]
  )

  const storeNo =
    storefront.fixedStoreNo ||
    searchParams.get('storeNo') ||
    store?.storeNo ||
    settings?.store?.default

  const tableNo =
    searchParams.get('table_no') ||
    table_no?.original

  const { data, isError, isSuccess, refetch } = useGetStore({
    // id: Number(storeNo),
    storeNo: storeNo ?? undefined,
  }, {
    enabled: !!storeNo,
  })

  useEffect(() => {
    if (isError) {
      resetStore()

      return
    }

    if (pathsToRefetch.includes(pathname) && store?.id) {
      void refetch()
    }
  }, [pathname, isError, pathsToRefetch, refetch, resetStore, store?.id])

  useEffect(() => {
    if (!isSuccess) return

    if (data) {
      const effectiveMenuCode = data.menu_code || settings?.menuCode
      const previousMenuContext = readStoreMenuContext(data.storeNo)
      const hasStoreContextChanged =
        (store != null &&
          (store.storeNo !== data.storeNo ||
          store.id !== data.id ||
            store.menu_code !== data.menu_code)) ||
        (previousMenuContext != null &&
          (previousMenuContext.storeId !== data.id ||
            previousMenuContext.menuCode !== effectiveMenuCode))

      if (hasStoreContextChanged) {
        initCart()
        void queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] !== 'useGetZMPSetting',
        })
      }

      if (data.storeNo && data.id != null && effectiveMenuCode) {
        persistStoreMenuContext({
          storeNo: data.storeNo,
          storeId: data.id,
          menuCode: effectiveMenuCode,
        })
      }

      setStore(data)
      updateCore({
        storeId: Number(data?.id),
      })
      if (tableNo) {
        setTableNo(tableNo)
      }
    }
  }, [
    data,
    initCart,
    isSuccess,
    setStore,
    setTableNo,
    settings?.menuCode,
    store,
    tableNo,
    updateCore,
  ])

  const mustResolveFixedStore =
    storefront.fixedStoreNo != null &&
    (!isSuccess || store?.storeNo !== storefront.fixedStoreNo)

  if (storefront.fixedStoreNo && isError) {
    return (
      <div role='alert' className='flex min-h-screen items-center justify-center p-6 text-center'>
        Không thể tải thông tin cửa hàng Phong Nha. Vui lòng thử lại.
      </div>
    )
  }

  if (mustResolveFixedStore) {
    return <div style={{ minHeight: '100vh' }} aria-busy='true' />
  }

  return (
    <>
      {children}
    </>
  )
}

export default AppStoreProvider
